/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  computed,
  contentChild,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { NgTemplateOutlet, isPlatformServer } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconGeolocationComponent, IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';

import maplibregl from 'maplibre-gl';
import { GeolocationService } from '../../geolocation.service';
import { StadiaMapsLoaderService } from '../stadia-maps-loader.service';
import { StadiaMapsContextService } from '../stadia-maps-context.service';
import { StadiaLatLng } from '../stadia-maps.types';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'pxw-stadia-maps',
  standalone: true,
  imports: [NgTemplateOutlet, IconGeolocationComponent, IconLoadingComponent],
  templateUrl: './stadia-maps.component.html',
  styleUrl: './stadia-maps.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StadiaMapsContextService],
})
export class StadiaMapsComponent implements AfterViewInit {
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private stadiaMapsLoaderService = inject(StadiaMapsLoaderService);
  private stadiaMapsContext = inject(StadiaMapsContextService);
  private geolocationService = inject(GeolocationService);

  mapRef = viewChild.required<ElementRef<HTMLElement>>('mapRef');
  mapContents = contentChild<TemplateRef<unknown>>('mapContents');

  readonly stadiaMapsApiKey = input<string>();
  readonly height = input<number>(300);
  readonly center = input<StadiaLatLng>({ lat: 0, lng: 0 });
  readonly zoom = input<number>(3);
  readonly displayMyLocationButton = input<boolean>(false);
  readonly geolocationErrorText = input.required<string>();

  readonly mapClick = output<StadiaLatLng>();
  readonly geolocationResult = output<StadiaLatLng>();

  readonly isMapReady = signal(false);
  readonly loadingGeolocation = signal(false);

  readonly mapElement = computed(() => this.mapRef().nativeElement);

  mapInstance: maplibregl.Map;

  @HostBinding('style.height.px')
  get styleHeight() {
    return this.height();
  }

  constructor() {
    combineLatest([
      toObservable(this.isMapReady),
      toObservable(this.center),
      toObservable(this.zoom),
    ])
      .pipe(
        filter(([isMapReady, center, zoom]) => Boolean(isMapReady && center && zoom)),
        untilDestroyed(this),
      )
      .subscribe(([isMapReady, center, zoom]) => {
        this.mapInstance.flyTo({
          center: center,
          zoom: zoom,
        });
      });
  }

  async ngAfterViewInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    await this.stadiaMapsLoaderService.loadResources(this.renderer);

    // Instantiate (and display) a map:
    this.mapInstance = new maplibregl.Map({
      container: this.mapElement(),
      style: 'https://cdn.pedix.app/MWfL7v9QDlts7y2E1jUw/osm_bright.json', // Style URL; see our documentation for more options
      center: this.center(), // Initial focus coordinate
      zoom: this.zoom(),
    });

    // Add zoom and rotation controls to the map.
    this.mapInstance.addControl(new maplibregl.NavigationControl(), 'bottom-right');

    this.mapInstance.on('click', event => {
      const coordinates: StadiaLatLng = {
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      };

      this.mapClick.emit(coordinates);
    });

    this.mapInstance.on('load', () => {
      this.isMapReady.set(true);
    });

    this.stadiaMapsContext.mapInstance = this.mapInstance;
  }

  protected async onGeolocateUser() {
    this.loadingGeolocation.set(true);

    try {
      const geolocationResult = await this.geolocationService
        .geolocate()
        .then(geolocationResult => {
          if (!geolocationResult) {
            return Promise.reject();
          }
          return geolocationResult;
        });
      this.geolocationResult.emit(geolocationResult);
    } catch (error) {
      if (error) {
        console.error(error);
      }
      alert(this.geolocationErrorText);
    }

    this.loadingGeolocation.set(false);
  }
}
